import React, { useState } from "react";
import {
    Dialog, DialogTitle, DialogContent, DialogActions,
    Box, Typography, Avatar, IconButton, Chip, Grid,
    TextField, MenuItem, Switch, Button, Alert, CircularProgress, Stack,
} from "@mui/material";
import EventNoteIcon from "@mui/icons-material/EventNote";
import AddIcon from "@mui/icons-material/Add";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";

import { LOYALTY_API_BASE, SECTION_COLORS } from "../../constants";
import { loyaltyFetch } from "../../hooks/loyaltyFetch";
import { useBrand } from "../../../../contexts/BrandContext";

const ORIGINS = ["INTERNAL", "EXTERNAL"];

const INITIAL_FORM = {
    key: "",
    origin: "INTERNAL",
    name: "",
    description: "",
    payload_fields: [], // [{ key: "", type: "string", description: "" }]
    active: true,
};

const CreateTransactionTypeDialog = ({ open, onClose, onCreated }) => {
    const { currentBrand } = useBrand();
    const brandName = currentBrand?.name || "";

    const [form, setForm] = useState({ ...INITIAL_FORM });
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    const handle = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));
    const isValid = form.origin.trim() && form.name.trim();

    const handleAddField = () => {
        setForm(p => ({
            ...p,
            payload_fields: [...p.payload_fields, { id: Date.now(), key: "", type: "string", description: "" }]
        }));
    };

    const handleUpdateField = (id, field, value) => {
        setForm(p => ({
            ...p,
            payload_fields: p.payload_fields.map(f => f.id === id ? { ...f, [field]: value } : f)
        }));
    };

    const handleRemoveField = (id) => {
        setForm(p => ({
            ...p,
            payload_fields: p.payload_fields.filter(f => f.id !== id)
        }));
    };

    const handleSubmit = async () => {
        if (!isValid) return;
        setSubmitting(true);
        setError(null);
        try {
            // Convert list to object
            const payloadSchema = {};
            form.payload_fields.forEach(f => {
                if (f.key.trim()) {
                    payloadSchema[f.key.trim()] = {
                        type: f.type,
                        description: f.description.trim() || undefined
                    };
                }
            });

            const body = {
                brand: brandName || null,
                origin: form.origin.trim(),
                name: form.name.trim(),
                description: form.description.trim() || undefined,
                payload_schema: Object.keys(payloadSchema).length > 0 ? payloadSchema : null,
                active: form.active,
            };
            const res = await loyaltyFetch(`${LOYALTY_API_BASE}/admin/transaction-types`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "X-Brand": brandName
                },
                body: JSON.stringify(body),
            });
            if (!res.ok) {
                const e = await res.json().catch(() => null);
                throw new Error(e?.detail || `Erreur HTTP ${res.status}`);
            }
            onCreated();
            handleClose();
        } catch (err) { setError(err.message); } finally { setSubmitting(false); }
    };

    const handleClose = () => { setForm({ ...INITIAL_FORM }); setError(null); onClose(); };

    const fieldSx = { "& .MuiOutlinedInput-root": { borderRadius: 2 } };
    const monoFieldSx = { ...fieldSx, "& textarea": { fontFamily: "monospace", fontSize: 12 } };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
            <DialogTitle sx={{ borderBottom: "2px solid #f1f3f5", px: 3, py: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <Box display="flex" alignItems="center" gap={1}>
                    <Avatar sx={{ width: 32, height: 32, background: SECTION_COLORS.transactionTypes.gradient }}>
                        <EventNoteIcon sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Typography variant="h6" fontFamily="Montserrat, sans-serif" fontWeight={700} color="#1a1a2e" fontSize={16}>
                        Nouveau Type de Transaction
                    </Typography>
                </Box>
                <IconButton onClick={handleClose} size="small"><CloseIcon /></IconButton>
            </DialogTitle>

            <DialogContent sx={{ pt: "24px !important", px: 3, pb: 2 }}>

                <Grid container spacing={2}>
                    {/* name */}
                    <Grid item xs={12}>
                        <TextField fullWidth required label="Nom affiché" value={form.name}
                            onChange={handle("name")} size="small" sx={fieldSx} />
                    </Grid>
                    {/* description */}
                    <Grid item xs={12}>
                        <TextField fullWidth label="Description" value={form.description}
                            onChange={handle("description")} size="small" multiline rows={2} sx={fieldSx} />
                    </Grid>
                    {/* payload_schema user friendly */}
                    <Grid item xs={12}>
                        <Box mt={1} p={2} sx={{ borderRadius: 2, bgcolor: "#f8f9fa", border: "1px dashed #ced4da" }}>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={1.5}>
                                <Typography variant="subtitle2" fontWeight={700} color="#495057">
                                    Structure des données (Payload)
                                </Typography>
                                <Button size="small" startIcon={<AddCircleOutlineIcon />} onClick={handleAddField}
                                    sx={{ color: SECTION_COLORS.transactionTypes.main, textTransform: "none", fontWeight: 600 }}>
                                    Ajouter un champ
                                </Button>
                            </Box>

                            {form.payload_fields.length === 0 ? (
                                <Typography fontSize={12} color="text.secondary" align="center" py={2}>
                                    Aucun champ personnalisé défini. Cliquez sur "Ajouter" pour commencer.
                                </Typography>
                            ) : (
                                <Stack spacing={1.5}>
                                    {form.payload_fields.map((f, index) => (
                                        <Box key={f.id} display="flex" gap={1} alignItems="flex-start">
                                            <TextField size="small" label="Nom du champ" value={f.key}
                                                onChange={(e) => handleUpdateField(f.id, "key", e.target.value)}
                                                placeholder="ex: order_id" sx={{ flex: 1, ...fieldSx }} />

                                            <TextField size="small" select label="Type" value={f.type}
                                                onChange={(e) => handleUpdateField(f.id, "type", e.target.value)}
                                                sx={{ width: 110, ...fieldSx }}>
                                                <MenuItem value="string">Texte</MenuItem>
                                                <MenuItem value="number">Nombre</MenuItem>
                                                <MenuItem value="boolean">Booléen</MenuItem>
                                            </TextField>

                                            <TextField size="small" label="Description" value={f.description}
                                                onChange={(e) => handleUpdateField(f.id, "description", e.target.value)}
                                                placeholder="Facultatif" sx={{ flex: 1.5, ...fieldSx }} />

                                            <IconButton size="small" color="error" onClick={() => handleRemoveField(f.id)} sx={{ mt: 0.5 }}>
                                                <DeleteOutlineIcon fontSize="small" />
                                            </IconButton>
                                        </Box>
                                    ))}
                                </Stack>
                            )}
                        </Box>
                    </Grid>

                    {/* active */}
                    <Grid item xs={12}>
                        <Box display="flex" alignItems="center" justifyContent="space-between" px={1.5} py={1}
                            sx={{ borderRadius: 2, bgcolor: "#fafbfd", border: "1px solid #e8ecf1" }}>
                            <Box>
                                <Typography fontSize={13} fontWeight={600} fontFamily="Montserrat, sans-serif">Activer</Typography>
                                <Typography fontSize={11} color="text.secondary">Disponible immédiatement</Typography>
                            </Box>
                            <Switch checked={form.active} onChange={() => setForm((p) => ({ ...p, active: !p.active }))}
                                sx={{
                                    "& .MuiSwitch-switchBase.Mui-checked": { color: SECTION_COLORS.transactionTypes.main },
                                    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": { bgcolor: SECTION_COLORS.transactionTypes.main }
                                }} />
                        </Box>
                    </Grid>
                </Grid>

                {error && <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>{error}</Alert>}
            </DialogContent>

            <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #e0e0e0", gap: 1 }}>
                <Button onClick={handleClose} sx={{ color: "text.secondary", textTransform: "none" }}>Annuler</Button>
                <Button variant="contained" onClick={handleSubmit} disabled={!isValid || submitting}
                    startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : <AddIcon />}
                    sx={{
                        background: SECTION_COLORS.transactionTypes.gradient, color: "#fff", textTransform: "none", borderRadius: 2, fontWeight: 600, px: 3,
                        "&:hover": { boxShadow: `0 4px 16px ${SECTION_COLORS.transactionTypes.main}40` }
                    }}>
                    {submitting ? "Création..." : "Créer"}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

export default CreateTransactionTypeDialog;
